import React, {
  createContext,
  ReactNode,
  useRef,
  useCallback,
  useMemo,
  useContext
} from 'react';

export type CacheController = ReturnType<typeof useCacheController>;

export const noopCacheController = {
  read(key: string, getValue: () => any) {
    return getValue();
  },
  clear() {}
};

const CacheContext = createContext<{
  read: <T extends unknown>(
    key: string,
    getValue: () => Promise<T>
  ) => Promise<T>;
  clear: (key?: string) => void;
}>(noopCacheController);

/**
 * A global key-value cache context provider
 *
 * Data fetchers can obtain a cache controller using the `useCacheController`
 * function. Useful for fetchers that fetch mostly static data that can be
 * reused. The functions returned are ref-stable so can safely be passed
 * as props and used as effect dependencies.
 *
 * ```js
 * const cacheController = useCacheController();
 *
 * const [{ data }] = useAbortablePromise(signal => {
 *   return cacheController.read('my-unique-cache-key', () => expensiveFetch({ signal }));
 * });
 * ```
 */
export function CacheProvider({ children }: { children: ReactNode }) {
  const cache = useRef(new Map());

  const read = useCallback(
    async <T,>(key: string, getValue: () => Promise<T>): Promise<T> => {
      const cachedValue = cache.current.get(key);

      if (cachedValue != null) {
        return cachedValue;
      }

      const result = await getValue();
      cache.current.set(key, result);
      return result;
    },
    []
  );

  const clear = useCallback((key?: string) => {
    if (key) {
      cache.current.delete(key);
      return;
    }

    cache.current.clear();
  }, []);

  const value = useMemo(() => ({ read, clear }), [read, clear]);

  return (
    <CacheContext.Provider value={value}>{children}</CacheContext.Provider>
  );
}

export function useCacheController() {
  return useContext(CacheContext);
}
